import React, { useEffect, useState } from "react";
import { ThemeBasedColorHeader, ThemeBasedColorMenubar } from "src/shared/modules/profile/theme/themeCheacker";
import { CategoryDetails, Order, ProductItem } from "src/shared/services/interface";
import { Button } from "../../components/Button";
import { notifyError } from "src/assets/toastService";
import InputBox from "../../components/InputBox";
import PopupModules from "src/features/auth/components/PopupModules";
import AddProduct from "./AddProduct";

export const statusColors: Record<string, string> = {
  Confirm: "bg-blue-200 text-blue-800",
  confirm: "bg-blue-200 text-blue-800",
  Inprocess: "bg-yellow-200 text-yellow-800",
  "Waiting for Delivery": "bg-orange-200 text-orange-800",
  Delivered: "bg-green-200 text-green-800",
  Cancelled: "bg-red-200 text-red-800",
  Cancel: "bg-red-200 text-red-800",
  RETURN: "bg-purple-200 text-purple-800",
  Refund: "bg-pink-200 text-pink-800",
};

type Props = {
  order: Order;
  received: number;
  remainingPrice: string;
  statusChange: (value: string) => void;
  orderStatus: string;
  setPayment: (data: boolean) => void;
  setPayOnly: (data: boolean) => void;
  setPrintData: (data: string) => void;
  setHistory: (data: boolean) => void;
  setLastOrderData: (data: Order) => void;
  osb: number | string;
  editData: boolean;
  setEditData: (data: boolean) => void;
  setOrderChange: (data: Order) => void;
  product: ProductItem[];
  category: CategoryDetails[];
  productSync: () => void;
  loadingPro: boolean;
  sendToOrder: (productId: string) => void;
};

export const statusOptions = ["Confirm", "Inprocess", "Waiting for Delivery", "Delivered", "Cancelled"];
const statusOption = ["Confirm", "Inprocess", "Waiting for Delivery", "Delivered"];

function OrderSidePanal({
  order,
  received,
  remainingPrice,
  statusChange,
  orderStatus,
  setPayOnly,
  setPayment,
  setPrintData,
  setHistory,
  setLastOrderData,
  osb,
  editData,
  setEditData,
  setOrderChange,
  category,
  product,
  loadingPro,
  productSync,
  sendToOrder,
}: Props) {
  const [productPrice, setProductPrice] = React.useState({
    orderPrice: "0.00",
    discount: "0.00",
    finalAmount: "0.00",
  });
  const [viewProduct, setViewProduct] = useState<boolean>(false);
  const now = new Date();
  const refundAmount =
    order?.paymentHistory?.reduce(
      (acc: number, p: any) => acc + (p.refundAmount || 0),
      0
    ) ?? 0;

  const adjustAmount =
    order?.paymentHistory?.reduce(
      (acc: number, p: any) => acc + (p.adjustAmount || 0),
      0
    ) ?? 0;
  const orderReturnAmount =
    order?.returnHistory?.reduce((acc, p) => {
      const itemTotal = p.items?.reduce((sum, i) => {
        return (
          sum +
          Number(i.netAmount)
        );
      }, 0);

      return acc + itemTotal;
    }, 0) ?? 0;

  const netTotal = order.Finalamount - orderReturnAmount;

  const renderDropdown = (value: string, title: string, name: string) => {
    const currentStatus = order?.OrderStatus;
    const filteredOptions = statusOption.filter((status) => {
      if (currentStatus === "Confirm") return status !== "Waiting for Delivery" && status !== "Delivered";
      if (currentStatus === "Inprocess") return status !== "Confirm" && status !== "Delivered";
      if (currentStatus === "Waiting for Delivery") return status !== "Confirm" && status !== "Inprocess";
      return true;
    });

    if (!order) {
      return (<div>Loading...</div>)
    }
    return (
      <div className='mb-3'>
        <p id={`${name}-label`} className={`text-left mb-2 text-xl px-2 py-1 rounded-md font-semibold ${ThemeBasedColorHeader()}`}>
          {title}
        </p>
        <select
          id={`${name}-select`}
          aria-labelledby={`${name}-label`}
          value={value}
          onChange={(e) => {
            statusChange(e.target.value);
          }}
          className={`w-full border border-gray-300 rounded px-2 py-2 text-lg min-h-[50px] ${ThemeBasedColorMenubar}`}
        >
          {filteredOptions.map((status) => (
            <option key={status} value={status} className='py-5'>
              {status}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const payment = () => {
    if (Number(osb) <= 0) {
      notifyError("Outstanding balance cleared via another order or ledger entry.");
      return;
    }
    setPayOnly(true);
    setPayment(true);
  };

  const rePrintData = () => {
    setHistory(false);
    setLastOrderData(order);
    setPrintData(now.toTimeString().split("T")[0]);
  };
  const orderPrice = order?.Products?.reduce((acc, item) => acc + item.totalPrice, 0) || 0;

  useEffect(() => {
    const discount = Number(productPrice.discount) || Number(order?.discount) || 0;
    const finalAmount = orderPrice - discount;
    setProductPrice({
      orderPrice: orderPrice.toFixed(2),
      discount: discount.toFixed(2),
      finalAmount: finalAmount.toFixed(2),
    });
  }, [order, orderPrice, productPrice.discount]);

  const addProduct = () => {
    setViewProduct(true);
  };

  const cancelEdit = () => {
    const finalAmount = orderPrice - Number(order?.discount);
    setProductPrice({
      orderPrice: orderPrice.toFixed(2),
      discount: Number(order.discount).toFixed(2),
      finalAmount: finalAmount.toFixed(2),
    });
    setEditData(false);
  };

  const isOsb = Number(osb) > 0;
  const totalSOB = Math.abs(Number(osb));
  return (
    <div
      className={`grid grid-cols-2 w-full lg:w-2/6 md:grid-cols-1 md:h-fit gap-2 h-full shadow-inner rounded-lg  p-2 ${ThemeBasedColorMenubar()}`}
    >
      <div className={`w-full bg-white rounded-xl shadow-lg p-2 h-fit`}>
        <h1 className={`text-left mb-2 text-xl px-2 py-1 rounded-md font-semibold ${ThemeBasedColorHeader()}`}>Order Details</h1>
        {[
          { label: "Order Id", value: order.Orderid },
          { label: "Name", value: order.CustomerName },
          {
            label: "Order Status",
            value: (
              <span className={`px-2 py-1 rounded font-semibold text-sm ${statusColors[order.OrderStatus] || "bg-gray-200 text-gray-700"}`}>
                {order.OrderStatus}
              </span>
            ),
          },
          { label: "Date", value: order.Orderdate },
          { label: "Time", value: order.Ordertime },
        ].map(({ label, value }) => (
          <div key={label} className='flex justify-between items-center'>
            <p>{label}</p>
            <p>{value}</p>
          </div>
        ))}
      </div>

      <div className={`w-full bg-white rounded-xl shadow-lg p-2 h-fit`}>
        <h1 className={`text-left mb-2 text-xl px-2 py-1 rounded-md font-semibold ${ThemeBasedColorHeader()}`}>
          Payment Details
        </h1>

        {/* BASIC */}
        {[
          { label: "Sub Total Price", value: productPrice.orderPrice },
          { label: "Discounts (-)", value: productPrice.discount, enabile: editData },
          { label: "Total Price", value: productPrice.finalAmount },
          ...(orderReturnAmount > 0
            ? [{ label: "Sum Return (-)", value: orderReturnAmount.toFixed(2) }]
            : []),
        ].map(({ label, value, enabile }) => (
          <div key={label} className='grid grid-cols-2 items-center'>
            <p>{label}</p>
            {enabile ? (
              <InputBox
                value={value}
                type='number'
                className='text-end'
                onChange={(e) =>
                  setProductPrice((prev) => ({
                    ...prev,
                    discount: e.target.value,
                  }))
                }
              />
            ) : (
              <p className='text-right'>{value}</p>
            )}
          </div>
        ))}


        {order?.returnHistory?.length && <div className="flex flex-col border-y">
          {[
            { label: "Net Total:", value: netTotal.toFixed(2) },
            { label: "Sum Received", value: received.toFixed(2) },
            { label: "Refund (-)", value: Number(refundAmount).toFixed(2) },

          ].map(({ label, value }) => (
            <div key={label} className='grid grid-cols-2 items-center mt-1'>
              <p>{label}</p>
              <p className='text-right'>{value}</p>
            </div>
          ))}
        </div>}

        {/* FINAL */}
        {[
          { label: "Net Received", value: (Number(received) - Number(refundAmount)).toFixed(2) },
          { label: "Remaining", value: Number(Number(remainingPrice) - adjustAmount).toFixed(2) },
          // { label: "OSB Adj ", value: adjustAmount.toFixed(2) },
          ...(isOsb
            ? [{ label: "Total OSB", value: Number(totalSOB).toFixed(2) }]
            : [{ label: "Advance", value: Number(totalSOB).toFixed(2) }]),
          {
            label: "Payment Type",
            value: order?.payments?.map((p) => p.Paymentmethod).join(", "),
          },
        ].map(({ label, value }) => (
          <div key={label} className='grid grid-cols-2 items-center mt-1'>
            <p>{label}</p>
            <p className='text-right'>{value}</p>
          </div>
        ))}
      </div>

      {order.OrderStatus !== "Delivered" && order.OrderStatus !== "Cancelled" && (
        <div className={`text-left mb-2 text-xl px-2 py-1 rounded-md font-semibold bg-white`}>
          {renderDropdown(orderStatus, "Update Order Status", "orderStatus")}
        </div>
      )}
      {editData ? (
        <div className='w-full justify-around grid grid-cols-1 sm:grid-cols-3 gap-1'>
          <Button label='Cancel' variant='cancel' onClick={cancelEdit} className='border shadow-md' />
          <Button
            label='Order Modify'
            variant='confirm'
            onClick={() => {
              setOrderChange({
                ...order,
                discount: productPrice.discount,
                Finalamount: Number(productPrice.finalAmount),
                totalAmount: Number(productPrice.orderPrice),
              });
            }}
            className='border shadow-md'
          />
          <Button label='Add Product' variant='ThemeBase' onClick={addProduct} className='border shadow-md' />
        </div>
      ) : (
        <div className='w-full justify-around grid grid-cols-1 sm:grid-cols-3 gap-1'>
          <Button label='Reprint' variant='secondary' onClick={rePrintData} className='border shadow-md' />
          <Button label='Payment History' onClick={() => setHistory(true)} disabled={order?.paymentHistory?.length === 1} />
          {order.OrderStatus !== "Cancelled" && (
            <Button label='Pay Balance' variant='confirm' disabled={Number(remainingPrice) === 0} onClick={payment} />
          )}
        </div>
      )}
      <PopupModules isOpen={viewProduct} onClose={() => setViewProduct(false)} title='Add Product' className='w-1/2 h-5/6'>
        <AddProduct
          sendToOrder={(productId) => sendToOrder(productId)}
          product={product}
          category={category}
          syncProduct={() => productSync()}
          loadingPro={loadingPro}
        />
      </PopupModules>
    </div>
  );
}

export default OrderSidePanal;
